import { useCallback, useState } from 'react';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { DropdownMenu, Icon, IconButton, Stack, tokens } from '@hotjar/hotsauce';

import { RemovalConfirmation } from './RemovalConfirmation';
import { RenameCollection } from './RenameCollection';

const ColoredText = styled.span<{ color?: React.ComponentProps<typeof Icon>['color'] }>`
  ${({ color }) =>
    `${
      color && color !== 'inherited' && tokens.ALIAS_TEXT_COLORS[color]
        ? `color: ${tokens.ALIAS_TEXT_COLORS[color]};`
        : ''
    }`}
`;

const ActionIcon = styled(Icon)`
  width: 16px;
  height: 16px;
  margin-right: ${tokens.spacer.x1};
`;

const IconButtonWithMargin = styled(IconButton)`
  margin-left: ${tokens.spacer.x1};
`;
// NOTE: this is needed to force displaying a dropdown/three dots menu for less actions than 4
// see the discussion here https://hotjar.slack.com/archives/CVCNLS3ML/p1630487440065400
interface CollectionActionsProps {
  className?: string;
  collection: {
    id: string;
    name: ComponentProps<typeof RemovalConfirmation>['collectionName'];
  };
  compact?: boolean;
  siteId?: ComponentProps<typeof RemovalConfirmation>['siteId'];
  onDropdownToggle?: (state: boolean) => void;
  isVisible: boolean;
}

export function CollectionActions({
  className,
  collection,
  compact = false,
  siteId,
  onDropdownToggle,
  isVisible,
}: CollectionActionsProps): JSX.Element {
  const { t } = useTranslation();
  const [isRemovalConfirmationOpen, setIsRemovalConfirmationOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEdit = useCallback(() => setIsEditModalOpen(true), [setIsEditModalOpen]);
  const handleRemove = useCallback(
    () => setIsRemovalConfirmationOpen(true),
    [setIsRemovalConfirmationOpen],
  );
  const markRemovalConfirmationAsClosed = useCallback(() => {
    setIsRemovalConfirmationOpen(false);
  }, [setIsRemovalConfirmationOpen]);

  const modal = (() => {
    if (siteId && isRemovalConfirmationOpen) {
      return (
        <RemovalConfirmation
          collectionId={collection.id}
          collectionName={collection.name}
          isOpen
          onClose={markRemovalConfirmationAsClosed}
          siteId={siteId}
        />
      );
    }

    if (siteId && isEditModalOpen) {
      return (
        <RenameCollection
          collectionId={collection.id}
          collectionName={collection.name}
          onClose={() => setIsEditModalOpen(false)}
          siteId={siteId}
        />
      );
    }

    return null;
  })();

  if (compact) {
    return (
      <>
        {isVisible ? (
          <DropdownMenu
            className={className}
            trigger={
              <IconButton
                icon="menu_kebab"
                kind="ghost"
                size="m"
                tooltipText={t('collections:actions.description')}
              />
            }
            appendTo="parent"
            onShown={() => onDropdownToggle?.(true)}
            onHidden={() => onDropdownToggle?.(false)}
          >
            <DropdownMenu.BaseItem onClick={handleEdit}>
              <ActionIcon icon="pencil" />
              <ColoredText>{t('collections:actions.edit')}</ColoredText>
            </DropdownMenu.BaseItem>

            <DropdownMenu.BaseItem onClick={handleRemove} hasDivider>
              <ActionIcon icon="trash" color="text_warning" />
              <ColoredText color="text_warning">{t('collections:actions.remove')}</ColoredText>
            </DropdownMenu.BaseItem>
          </DropdownMenu>
        ) : null}
        {modal}
      </>
    );
  }

  return (
    <>
      <Stack className={className} customSpacing="0">
        <IconButtonWithMargin
          icon="pencil"
          kind="secondary"
          onClick={handleEdit}
          size="m"
          tooltipText={t('collections:actions.edit')}
        />

        <IconButtonWithMargin
          icon="trash"
          kind="secondary"
          onClick={handleRemove}
          size="m"
          tooltipText={t('collections:actions.remove')}
        />
      </Stack>
      {modal}
    </>
  );
}
